import type { Dispatch, SetStateAction } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from './auth-store';
import type { RecoveryHealthPreview } from './recovery-health-types';

export type RecoveryReviewBucket = 'intact-progress' | 'needs-review' | 'account-loss';
interface ReviewState {
  results: RecoveryHealthPreview[];
  selected: string | null;
  bucket: RecoveryReviewBucket;
  total: number;
  hasScanned: boolean;
  drafts: Record<string, { subject: string; body: string }>;
}
const emptyState = (): ReviewState => ({ results: [], selected: null, bucket: 'intact-progress', total: 0, hasScanned: false, drafts: {} });

/** Keep review snapshots across route unmounts, scoped to the signed-in admin.
 * Explicit Recheck replaces the snapshot. Nothing is written to browser storage.
 */
export function useRecoveryReviewCache() {
  const adminId = useAuthStore(state => state.me?.discordId ?? 'anonymous');
  const client = useQueryClient();
  const queryKey = ['recovery-review-state', adminId];
  const { data = emptyState() } = useQuery({
    queryKey, queryFn: emptyState, enabled: false, staleTime: Infinity, gcTime: Infinity,
  });
  function setter<K extends keyof ReviewState>(field: K): Dispatch<SetStateAction<ReviewState[K]>> {
    return value => client.setQueryData<ReviewState>(queryKey, previous => {
      const state = previous ?? emptyState();
      return { ...state, [field]: typeof value === 'function'
        ? (value as (previous: ReviewState[K]) => ReviewState[K])(state[field]) : value };
    });
  }
  return { ...data, setResults: setter('results'), setSelected: setter('selected'), setBucket: setter('bucket'),
    setTotal: setter('total'), setHasScanned: setter('hasScanned'), setDrafts: setter('drafts') };
}
